import { Link } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { categoryGroups } from "@/mocks/categories";

const stats = [
  { value: String(videos.length), suffix: "部", label: "在架视频" },
  { value: String(categoryGroups.length), suffix: "大频道", label: "内容分类" },
  { value: "15", suffix: "个", label: "细分题材" },
  { value: "365", suffix: "天", label: "持续更新" },
];

const highlights = [
  {
    icon: "ri-hd-line",
    title: "高清画质",
    text: "封面与内容信息统一整理，清晰呈现每一部作品的视觉气质。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选",
    text: "按题材、地区、年份快速缩小范围，列表页还有更新时间与评分排序。",
  },
  {
    icon: "ri-device-line",
    title: "多端舒适",
    text: "桌面与手机都做了适配，单列卡片与折叠导航让操作更顺手。",
  },
];

export default function IntroSection() {
  return (
    <section className="page-shell py-12 md:py-16">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr] lg:gap-10">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-9">
          <p className="mb-3 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-400">
            <span className="h-px w-6 bg-primary-500" />
            About the platform
          </p>
          <h2 className="font-heading text-2xl font-semibold leading-snug tracking-tight text-foreground-950 md:text-[30px]">
            一个把电影、剧集和短视频
            <br className="hidden sm:block" />
            放在同一张桌面上的视频平台
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            逼特逼视频专注做一件事：让想看视频这件事变得简单。打开首页就有焦点推荐，
            想系统浏览就进入频道，想精确找片就用筛选和搜索。
            每条内容都配有完整的类型、地区、年份、导演与主演信息，列表与详情保持一致，不用来回确认。
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            当前站点为内容浏览演示版本，播放按钮会打开模拟播放器并给出明确提示，
            不接入真实视频流，也无需登录或付费。
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="rounded-md border border-background-200 bg-background-50 p-4"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded bg-secondary-500/15 text-secondary-300">
                  <i className={`${item.icon} text-base`} />
                </span>
                <h3 className="mt-3 font-heading text-sm font-medium text-foreground-900">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/category/movie"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              开始浏览电影
              <i className="ri-arrow-right-line text-base" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-400"
            >
              了解我们
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
            <h3 className="font-heading text-base font-medium text-foreground-900">平台数据</h3>
            <p className="mt-1 text-xs text-foreground-500">截至 2026 年 10 月的站内统计</p>
            <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-5">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <p className="font-heading text-2xl font-semibold text-primary-400">
                    {stat.value}
                    <span className="ml-0.5 font-body text-xs text-foreground-500">
                      {stat.suffix}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded bg-accent-500/15 text-accent-300">
                <i className="ri-flashlight-line text-base" />
              </span>
              <h3 className="font-heading text-base font-medium text-foreground-900">本日更新</h3>
            </div>
            <ul className="mt-4 space-y-3">
              {[...videos]
                .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
                .slice(0, 3)
                .map((video) => (
                  <li key={video.id}>
                    <Link
                      to={`/detail/${video.id}`}
                      className="flex items-center gap-3 rounded-md p-1 transition-colors hover:bg-background-100"
                    >
                      <img
                        src={video.cover}
                        alt={`${video.title}缩略图`}
                        className="h-12 w-20 shrink-0 rounded object-cover object-top"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-foreground-900">
                          {video.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-500">
                          {video.updatedAt} 更新 · {video.duration}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}